import type { BasicTableV3VirtualizationOptions } from "./BasicTableV3.types";

export const BASIC_TABLE_V3_NO_VIRTUALIZATION = "none";
export const DEFAULT_BASIC_TABLE_V3_SKELETON_ROW_COUNT = 7;
// Stable identity for tables without filter machinery: initialActiveFilters
// participates in provider state memoization, so an inline [] would
// re-initialize on every render.
export const BASIC_TABLE_V3_NO_ACTIVE_FILTERS: never[] = [];
/**
 * The header's label row: the `h-8` row holding the title, type icon and hover
 * chrome. This is the whole header height unless a column contributes a
 * `headerBand`, which adds its own declared height below it.
 */
export const BASIC_TABLE_V3_HEADER_LABEL_ROW_HEIGHT_PX = 32;
/** Height of the integrated super-header bar (`h-9`), when a table has one. */
export const BASIC_TABLE_V3_SUPER_HEADER_BAR_HEIGHT_PX = 36;
/**
 * The rail a clamped table's horizontal scrollbar lives in, below the scrollport
 * rather than inside it.
 *
 * A clamped scrollport's height is an exact row budget, and a native horizontal
 * scrollbar is laid out INSIDE the scrollport: it takes its own thickness out of
 * clientHeight, clipping the last row and making a table that exactly fits its
 * rows scrollable by that much. The block-end edge cannot be pre-reserved either
 * — `scrollbar-gutter: stable` only reserves the inline-end one. So the
 * scrollport clips overflow-x and a proxy scroller in this rail scrolls it.
 *
 * Because the rail is our own box, the bar's thickness is ours to declare rather
 * than measure: browsers fit the bar to the box (Chromium compresses its 11px
 * `thin` bar into a 10px rail), so the height stays an exact constant instead of
 * a platform-dependent measurement.
 */
export const BASIC_TABLE_V3_SCROLLBAR_RAIL_HEIGHT_PX = 10;
export const DEFAULT_BASIC_TABLE_V3_ESTIMATED_HEADER_HEIGHT =
  BASIC_TABLE_V3_HEADER_LABEL_ROW_HEIGHT_PX;
export const DEFAULT_BASIC_TABLE_V3_ESTIMATED_ROW_HEIGHT = 32;
export const DEFAULT_BASIC_TABLE_V3_MAX_VISIBLE_ROWS = 8;
export const DEFAULT_BASIC_TABLE_V3_FULL_PAGE_VIRTUALIZATION = {
  estimatedHeaderHeight: DEFAULT_BASIC_TABLE_V3_ESTIMATED_HEADER_HEIGHT,
  estimatedRowHeight: DEFAULT_BASIC_TABLE_V3_ESTIMATED_ROW_HEIGHT,
  mode: "full-page",
  overscan: 30,
} as const satisfies BasicTableV3VirtualizationOptions;
export const DEFAULT_BASIC_TABLE_V3_CLAMPED_VIRTUALIZATION = {
  estimatedHeaderHeight: DEFAULT_BASIC_TABLE_V3_ESTIMATED_HEADER_HEIGHT,
  estimatedRowHeight: DEFAULT_BASIC_TABLE_V3_ESTIMATED_ROW_HEIGHT,
  maxVisibleRows: DEFAULT_BASIC_TABLE_V3_MAX_VISIBLE_ROWS,
  mode: "clamped",
  overscan: 100,
} as const satisfies BasicTableV3VirtualizationOptions;
export const DEFAULT_VIRTUALIZATION_OVERSCAN = 100;
export const DEFAULT_BASIC_TABLE_V3_COLUMN_OVERSCAN = 3;
export const BASIC_TABLE_V3_HEADER_COLUMN_ID_ATTRIBUTE = "data-table-grid-drop-column-id";
// Matches LAYOUT_X_PADDING_CLASSNAME (12px): a viewport-filling table's bottom
// gap should read the same as the page's side gutters.
export const DEFAULT_VIEWPORT_FILL_BOTTOM_INSET_PX = 12;
// Menus this long are slow to work through one row at a time: value menus gain a
// search input, and Columns-menu sections gain "Select all" / "Deselect all".
export const BASIC_TABLE_V3_LONG_MENU_MIN_OPTIONS = 10;
// Default value-menu width; columns override via filterConfig.valueMenuWidthPx.
export const BASIC_TABLE_V3_VALUE_MENU_WIDTH_PX = 220;
// 150ms ease-out matches the design system's reveal timing (header filter
// buttons, menu affordances); filter chips enter, exit, and glide with it.
export const BASIC_TABLE_V3_FILTER_CHIP_TRANSITION = {
  duration: 0.15,
  ease: "easeOut",
} as const;

// The header/body cell treatments (padding, type, surface shades), shared by
// BasicTableV3's <th>/<td> shells and EditableBasicTableV3 so the editable
// table cannot drift visually from the read-only one. Effective dark header
// text also flows from the global `.dark th` rule in dark-mode.css.
export const BASIC_TABLE_V3_CELL_HORIZONTAL_PADDING_CLASS = "px-cds-10";
export const BASIC_TABLE_V3_HEADER_CELL_CLASS =
  `h-8 ${BASIC_TABLE_V3_CELL_HORIZONTAL_PADDING_CLASS} text-left text-cds-sm font-medium text-cds-ink-subtle ` +
  "bg-cds-surface-active dark:bg-cds-raised-dark dark:text-zinc-500";
// Row surfaces cover the natural slot left behind by transparent sticky action cells.
export const BASIC_TABLE_V3_BODY_ROW_CLASS =
  "bg-cds-surface-subtle dark:bg-cds-elevated-dark " +
  "hover:bg-cds-surface-active dark:hover:bg-cds-raised-dark " +
  "[&:hover>td]:bg-cds-surface-active dark:[&:hover>td]:bg-cds-raised-dark";
export const BASIC_TABLE_V3_BODY_CELL_CLASS =
  `${BASIC_TABLE_V3_CELL_HORIZONTAL_PADDING_CLASS} text-cds-sm text-cds-ink bg-cds-surface-subtle ` +
  "dark:bg-cds-elevated-dark dark:text-zinc-200";
// Outer frame around the table surface (framed mode).
export const BASIC_TABLE_V3_FRAME_CLASS =
  "rounded-cds-6 overflow-hidden border-[1px] border-cds-surface-border dark:border-cds-ink";
export const TABLE_GRID_STROKE_BACKGROUND_CLASS = "bg-cds-surface-border dark:bg-cds-ink";
export const GRIDLINE_SEPARATOR_CLASS = `absolute inset-y-0 right-0 w-px ${TABLE_GRID_STROKE_BACKGROUND_CLASS}`;
export const GRIDLINE_SEPARATOR_HIGHLIGHT_CLASS = "!bg-zinc-300 dark:!bg-zinc-700";
export const HORIZONTAL_SEPARATOR_CLASS = `absolute inset-x-0 bottom-0 h-px ${TABLE_GRID_STROKE_BACKGROUND_CLASS}`;
export const HORIZONTAL_SEPARATOR_TOP_CLASS = `absolute inset-x-0 top-0 h-px ${TABLE_GRID_STROKE_BACKGROUND_CLASS}`;
export const HEADER_BAND_SEPARATOR_CLASS =
  "pointer-events-none absolute inset-x-0 z-[5] h-px bg-cds-surface-border dark:bg-white/[0.08]";
export const DROP_TARGET_BEFORE_CLASS =
  "after:content-[''] after:pointer-events-none after:absolute after:inset-y-0 after:left-0 after:z-[10] after:w-0.5 after:bg-cds-primary-stroke-default";
export const DROP_TARGET_AFTER_CLASS =
  "after:content-[''] after:pointer-events-none after:absolute after:inset-y-0 after:right-0 after:z-[10] after:w-0.5 after:bg-cds-primary-stroke-default";
export const MENU_DROP_TARGET_BEFORE_CLASS =
  "after:content-[''] after:pointer-events-none after:absolute after:inset-x-0 after:top-0 after:z-[10] after:h-0.5 after:bg-cds-primary-stroke-default";
export const MENU_DROP_TARGET_AFTER_CLASS =
  "after:content-[''] after:pointer-events-none after:absolute after:inset-x-0 after:bottom-0 after:z-[10] after:h-0.5 after:bg-cds-primary-stroke-default";
export const MENU_SECTION_DROP_TARGET_CLASS =
  "rounded-cds-3 bg-cds-surface-hover dark:bg-white/[0.06]";
export const SOURCE_COLUMN_DRAG_CLASS =
  "outline outline-1 -outline-offset-1 outline-cds-primary-stroke-default";
export const TABLE_FROZEN_EDGE_SHADOW_CLASS =
  "pointer-events-none absolute inset-y-0 z-[50] w-cds-16 bg-gradient-to-r from-black/5 to-transparent dark:from-black/30";
// Scroll cliff under the header. The caller sets `top` from the header's
// MEASURED height: a header with a band (distribution strip) is taller than the
// label row, and a hardcoded offset would draw this gradient across the band.
export const TABLE_HEADER_EDGE_SHADOW_CLASS =
  "pointer-events-none absolute inset-x-0 z-[50] h-cds-16 bg-gradient-to-b from-black/5 to-transparent dark:from-black/30";
export const TABLE_RIGHT_EDGE_SHADOW_CLASS =
  "pointer-events-none absolute inset-y-0 z-[50] w-cds-16 bg-gradient-to-l from-black/5 to-transparent dark:from-black/30";
export const FROZEN_EDGE_SEPARATOR_CLASS = `pointer-events-none absolute inset-y-0 z-[55] w-px ${TABLE_GRID_STROKE_BACKGROUND_CLASS}`;
// Overlay right pins (stickyRightPresentation="overlay") key off the table root's
// data-basic-table-v3-right-underlay, set only while columns scroll beneath the right edge. At the
// right scroll edge nothing is beneath them, so they render as ordinary cells in their own slot.
// Class names stay whole literals so Tailwind's scanner can see them.
export const BASIC_TABLE_V3_ROOT_GROUP_CLASS = "group/basic-table-v3";
// While underlaid, clears the cell's own surface so the glass layer behind its controls shows
// through. Important because row hover/focus paint every cell through higher-specificity row
// selectors. --basic-table-v3-overlay-reveal (0 or 1) shows the controls and glass only while the
// row is hovered, while the cell's menu is open (its trigger sets aria-expanded and the menu portals
// away from the row), always on devices that cannot hover, and while the row holds keyboard focus.
// Keyboard focus excludes menu triggers' native :focus-visible: a menu hands focus back to its
// trigger when it closes, which Chrome reports as :focus-visible even after a mouse interaction.
// Headless UI's data-focus on the trigger follows the real input modality instead.
export const BASIC_TABLE_V3_OVERLAY_CELL_CLASS =
  "group-data-[basic-table-v3-right-underlay=true]/basic-table-v3:!bg-transparent " +
  "[--basic-table-v3-overlay-reveal:0] " +
  "group-hover/basic-table-v3-row:[--basic-table-v3-overlay-reveal:1] " +
  "group-focus-visible/basic-table-v3-row:[--basic-table-v3-overlay-reveal:1] " +
  "group-has-[:focus-visible:not([aria-haspopup])]/basic-table-v3-row:[--basic-table-v3-overlay-reveal:1] " +
  "has-[[aria-haspopup][data-focus]]:[--basic-table-v3-overlay-reveal:1] " +
  "has-[[aria-expanded=true]]:[--basic-table-v3-overlay-reveal:1] " +
  "[@media(hover:none)]:[--basic-table-v3-overlay-reveal:1]";
// Positioned after the glass so the controls paint above it.
export const BASIC_TABLE_V3_OVERLAY_CONTROLS_CLASS =
  "relative opacity-[var(--basic-table-v3-overlay-reveal)] transition-opacity duration-100";
// Header and footer overlay cells hold no controls; while underlaid they turn transparent and let
// clicks through to the header labels and resize handles scrolling beneath them.
export const BASIC_TABLE_V3_OVERLAY_CHROME_CELL_CLASS =
  "group-data-[basic-table-v3-right-underlay=true]/basic-table-v3:!bg-transparent " +
  "group-data-[basic-table-v3-right-underlay=true]/basic-table-v3:pointer-events-none";
// Glass behind a row's overlay controls: the row surface at high opacity, feathered across the
// column to its left so content fades out under the controls instead of meeting a hard column edge.
// No backdrop-filter: one blur layer per mounted row exhausts Chrome's compositor on full-page
// tables, which then blanks large regions of the page. The cell's controls are positioned after
// this layer so they paint on top of it.
export const BASIC_TABLE_V3_OVERLAY_GLASS_CLASS =
  "pointer-events-none absolute inset-y-0 -left-cds-24 right-0 z-0 opacity-0 transition-opacity duration-100 " +
  "[mask-image:linear-gradient(to_right,transparent,black_24px)] " +
  "bg-cds-surface-subtle/90 group-hover/basic-table-v3-row:bg-cds-surface-active/90 " +
  "dark:bg-cds-elevated-dark/90 dark:group-hover/basic-table-v3-row:bg-cds-raised-dark/90 " +
  "group-data-[basic-table-v3-right-underlay=true]/basic-table-v3:opacity-[var(--basic-table-v3-overlay-reveal)]";
// Header hover chrome (sort icon, filter button, drag handle) reveals as a
// PURE FADE while its slot collapses at rest, so the resting label keeps the
// full column width and nothing ever slides horizontally. CSS transitions
// read the destination state's transition-property, so the contract is split:
// revealing lands on a state that transitions opacity only (the slot snaps
// open in the same frame), while hiding lands on the resting state, whose
// transition DELAYS the max-width/margin collapse past the opacity fade.
export const TABLE_HEADER_CHROME_SHOWN_TRANSITION_CLASS =
  "transition-opacity duration-150 ease-out";
export const TABLE_HEADER_CHROME_HIDDEN_TRANSITION_CLASS =
  "[transition:opacity_150ms_ease-out,max-width_0s_150ms,margin_0s_150ms]";
export const TABLE_HEADER_CHROME_HOVER_REVEAL_TRANSITION_CLASS =
  "group-hover/header:[transition:opacity_150ms_ease-out] " +
  "group-focus-within/header:[transition:opacity_150ms_ease-out]";
export const TABLE_DRAG_HANDLE_CLASS =
  "inline-flex shrink-0 cursor-grab items-center justify-center active:cursor-grabbing";
export const TABLE_DRAG_HANDLE_ACTIVE_CLASS = "!cursor-grabbing [&_*]:!cursor-grabbing";
export const TABLE_DRAG_HANDLE_ICON_CLASS =
  "shrink-0 text-cds-ink-muted transition-[color,opacity] duration-75 dark:text-zinc-500";
export const TABLE_RESIZE_HANDLE_LINE_CLASS =
  "before:absolute before:inset-y-0 before:left-1/2 before:w-px before:-translate-x-1/2 " +
  "before:bg-transparent before:transition-colors before:duration-75 " +
  "hover:before:bg-zinc-300 dark:hover:before:bg-zinc-700";
